import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useFetchWithAuth } from '../../session/useFetchWithAuth'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface CreateCityInput {
  name: string
}

interface UpdateCityInput {
  name: string
}

export const useCreateCityMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateCityInput) => {
      const response = await fetchWithAuth(`${API_URL}/admin/cities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      return response
    },
    onError: (error: any) => {
      console.error('Create city error:', error)
      if (error.message?.includes('already exists') || error.status === 409) {
        toast.error(error.message || 'This county already exists')
      } else {
        toast.error(error.message || 'Failed to create county')
      }
    },
    onSuccess: () => {
      toast.success('County created successfully')
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
    },
  })
}

export const useUpdateCityMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      name,
      data,
    }: {
      name: string
      data: UpdateCityInput
    }) => {
      const response = await fetchWithAuth(`${API_URL}/admin/cities/${name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      return response
    },
    onError: (error: any) => {
      console.error('Update city error:', error)
      if (error.message?.includes('already exists') || error.status === 409) {
        toast.error(error.message || 'This county already exists')
      } else {
        toast.error(error.message || 'Failed to update county')
      }
    },
    onSuccess: () => {
      toast.success('County updated successfully')
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
    },
  })
}

export const useDeleteCityMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (name: string) => {
      await fetchWithAuth(`${API_URL}/admin/cities/${name}`, {
        method: 'DELETE',
      })
    },
    onError: (error: any) => {
      console.error('Delete city error:', error)
      toast.error(error.message || 'Failed to delete county')
    },
    onSuccess: () => {
      toast.success('County deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
    },
  })
}
